import React, { useState, useContext } from "react";
import { Dialog } from "@headlessui/react";
import CommonButton from "../../components/common/CommonButton";
import AppContext from "../../context/AppContext";
import { toast } from "../../utils/toast";
import { uploadExcel } from "../../service/Task";
import translations from "../../components/common/Translations";

const MappingSection = ({ title, cols, colHeaders, mapping, handleMapChange }) => (
    <div className="mb-6">
        <h3 className="font-medium text-gray-700 mb-4">{title}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {cols.map((col, i) => (
                <div key={i} className="flex flex-col space-y-2">
                    <span className="text-sm font-medium text-gray-600">{col}</span>
                    <select
                        className="border text-sm border-gray-300 rounded-lg px-3  text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-150 ease-in-out"
                        value={mapping[col] || ""}
                        onChange={(e) => handleMapChange(col, e)}
                    >
                        <option value="">Select a column</option>
                        {colHeaders
                            .filter(
                                (header) =>
                                    !Object.values(mapping).includes(header) || mapping[col] === header
                            )
                            .map((header, index) => (
                                <option key={index} value={header}>
                                    {header}
                                </option>
                            ))}
                    </select>
                </div>
            ))}
        </div>
    </div>
);

const ColumnMap = ({ open, setOpen, mandatoryCols, otherCols, file, colHeaders }) => {
    const { projectId, selectedOrgId, langMode } = useContext(AppContext);
    const [mapping, setMapping] = useState({});
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    const handleMapChange = (sysCol, e) => {
        setMapping({ ...mapping, [sysCol]: e.target.value });
    };

    const isMandatoryColsMapped = mandatoryCols.every((col) => mapping[col]);

    const handleNext = () => {
        if (isMandatoryColsMapped) {
            setCurrentPage(2);
        } else {
            toast.error(translations[langMode].pleasefillallmandtoryfield);
        }
    };

    const handleBack = () => {
        setCurrentPage(1);
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {

            const res = await uploadExcel(file, projectId, selectedOrgId, mapping);
            if (res?.header?.code === 600) {
                toast.success(res?.body?.value);
                setOpen(false);
            } else {
                toast.error(res?.body?.value || "Error uploading excel");
            }
        } catch (err) {
            toast.error(err?.body?.error || "Error uploading excel");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => setOpen(false)} className="relative z-50">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="bg-white p-8 rounded-lg shadow-2xl w-full max-w-4xl transform transition-all h-[400px] overflow-y-auto">
                    <Dialog.Title className="text-xl mb-6 font-bold text-gray-900 ">{translations[langMode].importdb}</Dialog.Title>

                    {currentPage === 1 && (
                        <MappingSection
                            title={translations[langMode].MandatoryFields}
                            cols={mandatoryCols}
                            colHeaders={colHeaders}
                            mapping={mapping}
                            handleMapChange={handleMapChange}
                        />
                    )}
                    {currentPage === 2 && (
                        <MappingSection
                            title={translations[langMode].NonMandatoryFields}
                            cols={otherCols}
                            colHeaders={colHeaders}
                            mapping={mapping}
                            handleMapChange={handleMapChange}
                        />
                    )}

                    <div className="mt-8 flex justify-end gap-3">
                        <CommonButton colorIntent="danger" variant="danger" onClick={() => setOpen(false)} tooltipText={translations[langMode].cancelandclose} tooltipPosition="left" >
                            {translations[langMode].cancle}
                        </CommonButton>
                        {currentPage === 2 && (
                            <CommonButton colorIntent="primary"
                                variant="warning"
                                onClick={handleBack} 
                                tooltipText={translations[langMode].gobacktomapmandotoryfield}
                                tooltipPosition="left"
                                 >
                                {translations[langMode].Back}
                            </CommonButton>
                        )}



                        {currentPage === 1 && (
                            <CommonButton colorIntent="primary"
                                variant="primary"
                                onClick={handleNext}
                                tooltipText={isMandatoryColsMapped ? translations[langMode].proceedtomapnonmdfield : translations[langMode].proceedtomapnonmdfield}
                                tooltipPosition="left"
                            >
                                {translations[langMode].next}
                            </CommonButton>
                        )}

                        {currentPage === 2 && (
                            <CommonButton colorIntent="success"
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={loading}
                                tooltipText={translations[langMode].submitthemapedcol}
                                tooltipPosition="left"
                            >
                                {loading ? translations[langMode].submitting : translations[langMode].submit}
                            </CommonButton>
                        )}
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
};

export default ColumnMap;
